import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';
import { usePickemStore } from '@/store/usePickem.store.ts';
import { useEffect, useState } from 'react';
import {
  LeaderboardItem,
  OptionType,
  PickemLeaderboardData,
  SelectionType,
  TournamentLeaderboardData,
} from '@/@types/api.types.ts';
import { useGetLeagueTournamentLeaderboard } from '@/api/league-tournament-leaderboard/getLeaderboard.ts';
import { useGetPickem } from '@/api/pickems/getPickem.ts';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Card,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { TournamentControlBar } from '@/pages/LeaderboardPage/TournamentControlBar.tsx';
import { InfiniteTable } from '@/pages/LeaderboardPage/InfiniteTable.tsx';

export const TournamentLeaderboard = () => {
  const { pickemId, leagueId, tournamentId } = useAppSearchParams();

  const { checkedUsers, setCheckedUsers } = usePickemStore();

  const [leaderboardData, setLeaderboardData] = useState<
    PickemLeaderboardData | TournamentLeaderboardData | null
  >(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardItem[]>([]);

  const {
    data: tournamentLeaderboardData,
    isLoading: tournamentIsLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetLeagueTournamentLeaderboard({
    leagueId,
    tournamentId,
    offset: 0,
    limit: 50,
  });

  const { data: pickem } = useGetPickem(pickemId);

  const dataSource = tournamentLeaderboardData;

  useEffect(() => {
    setLeaderboardData(dataSource ?? null);
    setLeaderboard(dataSource?.leaderboard ?? []);
  }, [dataSource]);

  useEffect(() => {
    if (!pickem || !dataSource) return;

    const answers_leaderboard = dataSource.leaderboard.map(
      (userLeaderboard) => {
        const answers = userLeaderboard.selections.reduce(
          (accum, selection: SelectionType) => {
            const question = pickem.questions.find(
              (item) => item.question_id === selection.question_id
            );
            if (question) {
              if (question.is_tiebreak) {
                accum.custom_value =
                  +question.tiebreak_score === +selection.custom_value;
              }
              const option = question.options.find(
                (item: OptionType) => item.option_id === selection.option_id
              );
              if (option) {
                if (!!option.is_correct) {
                  accum.correct++;
                }
                if (!option.is_correct) {
                  accum.incorrect++;
                }
              }
            }
            return accum;
          },
          { correct: 0, incorrect: 0, custom_value: false }
        );
        return { ...userLeaderboard, answers };
      }
    );

    setLeaderboardData({
      ...dataSource,
      leaderboard: answers_leaderboard,
    } as any);
  }, [pickem, dataSource]);

  const handleCheckUser = (checked: boolean, user_id: number) => {
    if (checked) {
      checkedUsers.push(user_id);
      setCheckedUsers([...checkedUsers]);
    } else {
      const newCheckedUsers = checkedUsers.filter((item) => item !== user_id);
      setCheckedUsers([...newCheckedUsers]);
    }
  };

  return (
    <>
      <Helmet>
        <title>Leaderboard </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Tournament Leaderboard View
          </Typography>
        </Stack>

        <TournamentControlBar
          leaderboardData={leaderboardData as TournamentLeaderboardData}
        />

        <Card>
          {tournamentIsLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <InfiniteTable
              checkedUsers={checkedUsers}
              data={leaderboard}
              hasMore={hasNextPage}
              loadMore={fetchNextPage}
              handleCheckUser={handleCheckUser}
            />
          )}
        </Card>
      </Container>
    </>
  );
};
